import { ANALYTICS_MAX_BATCH_SIZE, type AnalyticsEventWire, postAnalyticsBatch } from "./api.ts";
import { type AnalyticsEventName, isPreAuthAnalyticsEvent } from "./catalog.ts";

const FLUSH_INTERVAL_MS = 5_000;
const FLUSH_AT_QUEUE_SIZE = 10;
/** Bounded so a guest who never resolves consent can't grow this forever. */
const MAX_PENDING_CONSENT_BUFFER = 50;

export type AnalyticsConsentStatus = "unknown" | "accepted" | "declined";

export interface TrackContext {
  venueId?: string;
  branchId?: string;
  venueSessionId?: string;
}

function randomId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `evt-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

type SendFn = (events: AnalyticsEventWire[], options?: { keepalive?: boolean }) => Promise<unknown>;

/**
 * Client analytics batching (FR-ANALYTICS-001/002/003/006). Batches
 * `track()` calls, flushes on a ~5s interval, at 10 queued events, or on
 * page-hide (keepalive fetch), and silently drops a batch on failure —
 * analytics must never break UX.
 *
 * ASSUMPTION (CLAUDE.md ASSUMPTION protocol): no durable offline queue here.
 * FR-ANALYTICS-006 asks for offline durability with retry-with-backoff; this
 * MVP pass keeps the queue in-memory only (matches the Part-1 "no IndexedDB
 * queue" scope note) and just drops a batch that fails to send, rather than
 * persisting/retrying it. A reload loses any not-yet-flushed events.
 */
export class AnalyticsClient {
  private consent: AnalyticsConsentStatus = "unknown";
  private queue: AnalyticsEventWire[] = [];
  private pendingConsentBuffer: AnalyticsEventWire[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly clientId = randomId();
  private readonly send: SendFn;

  constructor(send: SendFn = postAnalyticsBatch) {
    this.send = send;
  }

  /** Idempotent — safe to call once at app boot (see `main.tsx`). */
  start(): void {
    if (this.timer || typeof window === "undefined") return;
    this.timer = setInterval(() => void this.flush(), FLUSH_INTERVAL_MS);
    window.addEventListener("pagehide", this.handleHide);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    if (typeof window !== "undefined") {
      window.removeEventListener("pagehide", this.handleHide);
      document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    }
  }

  private handleHide = (): void => {
    void this.flush({ keepalive: true });
  };

  private handleVisibilityChange = (): void => {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      void this.flush({ keepalive: true });
    }
  };

  /**
   * Consent resolution (FR-ANALYTICS-003/011): accepting flushes any
   * non-essential events buffered while consent was unresolved; declining
   * discards them. Either way, future non-essential `track()` calls are
   * gated by the new status going forward.
   */
  setConsent(accepted: boolean): void {
    this.consent = accepted ? "accepted" : "declined";
    if (accepted && this.pendingConsentBuffer.length > 0) {
      this.queue.push(...this.pendingConsentBuffer);
      this.pendingConsentBuffer = [];
      if (this.queue.length >= FLUSH_AT_QUEUE_SIZE) void this.flush();
    } else {
      this.pendingConsentBuffer = [];
    }
  }

  getConsentStatus(): AnalyticsConsentStatus {
    return this.consent;
  }

  track(
    name: AnalyticsEventName,
    properties?: Record<string, unknown>,
    context: TrackContext = {},
  ): void {
    const essential = isPreAuthAnalyticsEvent(name);
    if (!essential && this.consent !== "accepted") {
      if (this.consent === "declined") return; // dropped: consent declined (FR-ANALYTICS-011)
      if (this.pendingConsentBuffer.length >= MAX_PENDING_CONSENT_BUFFER) {
        this.pendingConsentBuffer.shift(); // bounded buffer: drop oldest, never grow unbounded
      }
      this.pendingConsentBuffer.push(this.toWire(name, properties, context));
      return;
    }

    this.queue.push(this.toWire(name, properties, context));
    if (this.queue.length >= FLUSH_AT_QUEUE_SIZE) void this.flush();
  }

  private toWire(
    name: AnalyticsEventName,
    properties: Record<string, unknown> | undefined,
    context: TrackContext,
  ): AnalyticsEventWire {
    return {
      eventId: randomId(),
      eventName: name,
      occurredAt: new Date().toISOString(),
      properties: properties ?? {},
      venueId: context.venueId,
      branchId: context.branchId,
      venueSessionId: context.venueSessionId,
      clientId: this.clientId,
    };
  }

  async flush(options: { keepalive?: boolean } = {}): Promise<void> {
    if (this.queue.length === 0) return;
    const batch = this.queue.splice(0, ANALYTICS_MAX_BATCH_SIZE);
    try {
      await this.send(batch, options);
    } catch {
      // Silently dropped: analytics must never break UX (see class docs).
    }
  }

  /** Test-only: clears all in-memory state. */
  reset(): void {
    this.consent = "unknown";
    this.queue = [];
    this.pendingConsentBuffer = [];
  }

  /** Test/inspection-only: current ready-to-flush queue length. */
  get queueLength(): number {
    return this.queue.length;
  }

  /** Test/inspection-only: current pre-consent buffered length. */
  get pendingConsentLength(): number {
    return this.pendingConsentBuffer.length;
  }
}

export const analyticsClient = new AnalyticsClient();
